import React, {useState, useEffect} from 'react';
import clx from 'classnames';
import wrap from 'wraptile';
import CircularProgress from '@material-ui/core/CircularProgress';
import Paper from '@material-ui/core/Paper';
import MUITable from '@material-ui/core/Table';
import MUITableContainer from '@material-ui/core/TableContainer';
import TableFooter from '@material-ui/core/TableFooter';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import {curry} from 'ramda';
import {v4 as uuidv4} from 'uuid';
import tryCatch from 'try-catch';
import useQuery from '@pluto/query-hook';

import {
    OnChangePageHandler,
    SetPage,
    SetRowsPerPage,
    SetSort,
    ColumnType,
    Sort,
    TableVariants,
} from './tableTypes';
import TableBodyContainer from './TableBodyContainer';
import TableHeader from './tableHeader/TableHeader';
import './Table.scss';

const {parse} = JSON;

interface TableContainerProps<T> {
    data: T[];
    isLoading?: boolean;
    columns: ColumnType[];
    total?: number;
    removedIds?: string[];
    onChangePageHandler?: OnChangePageHandler;
    rowsPerPage?: number;
    setRowsPerPage?: SetRowsPerPage;
    page?: number;
    setPage?: SetPage;
    setSort?: SetSort;
    sort?: Sort;
    children?: React.ReactNode;
    variant?: TableVariants;
}

interface RowsGetArgs<T> {
    page: number;
    total?: number;
    data: T[];
    rowsPerPage: number;
}

interface ErrorDetails {
    details?: string[];
}

export const handleChangeRowsPerPage = curry((handlers, event) => {
    const limit = Number(event.target.value);
    const {onChangePageHandler, setRowsPerPage, setPage} = handlers;

    sessionStorage.setItem('rowsPerPage', String(limit));
    setRowsPerPage(limit);
    setPage(0);
    onChangePageHandler({
        limit,
        offset: 0,
    });
});

export const handleChangePage = curry((handlers, event, newPage) => {
    const {onChangePageHandler, setPage, rowsPerPage} = handlers;
    const offset = newPage * rowsPerPage;

    setPage(newPage);
    onChangePageHandler({
        limit: rowsPerPage,
        offset,
    });
});

const sliceData = (data: unknown[], rowsPerPage: number): unknown[] =>
    data.slice(0, rowsPerPage);

function addId<T>(item: T) {
    return {
        id: uuidv4(),
        ...item,
    };
}

export function appendUniqueKeys<T>(data: T[]) {
    return data.map(addId);
}

export function getDetailsErrorData(data: any, rowsPerPage: number): unknown[] {
    if (typeof data !== 'object' || !data.errors) return [];

    const withKeys = appendUniqueKeys(data.errors);
    return sliceData(withKeys, rowsPerPage);
}

export function getErrorsList<T extends ErrorDetails>(data: T[], rowsPerPage: number) {
    const [error] = data;
    const [parseError, errorsData] = tryCatch(parse, error.details);

    if (parseError) return [parseError.message];

    return [
        ...sliceData(data, rowsPerPage),
        ...getDetailsErrorData(errorsData.data, rowsPerPage),
    ];
}

export function getRows<T extends ErrorDetails>(args: RowsGetArgs<T>) {
    const {page, total, data, rowsPerPage} = args;

    if (!data.length) return data;

    const [error] = data;

    if (error.details) {
        return getErrorsList(data, rowsPerPage);
    }

    if (!total) {
        const n = page * rowsPerPage;
        return data.slice(n, n + rowsPerPage);
    }

    return data.slice(0, rowsPerPage);
}

export const handleMount = wrap((query: URLSearchParams) => {
    const storagedRowsPerPage = sessionStorage.getItem('rowsPerPage');
    const queryLimit = query.get('limit');

    if (queryLimit && queryLimit !== storagedRowsPerPage) {
        return sessionStorage.setItem('rowsPerPage', queryLimit);
    }

    if (!storagedRowsPerPage) {
        return sessionStorage.setItem('rowsPerPage', '25');
    }
});

export default function TableContainer<T>(props: TableContainerProps<T>) {
    const storagedRowsPerPage = Number(sessionStorage.getItem('rowsPerPage'));
    const rowsOptions = [25, 50, 100];
    const query = useQuery();
    const [stateRows, stateSetRows] = useState(storagedRowsPerPage || rowsOptions[0]);
    const [statePage, stateSetPage] = useState(0);
    const {
        data,
        isLoading = false,
        columns,
        total = 0,
        removedIds = [],
        onChangePageHandler = () => {},
        rowsPerPage = stateRows,
        setRowsPerPage = stateSetRows,
        page = statePage,
        setPage = stateSetPage,
        setSort,
        sort,
        children = null,
        variant,
    } = props;
    const onHandleMount = handleMount(query);

    useEffect(onHandleMount, []);

    if (isLoading) return <CircularProgress />;

    const renderRows = getRows({
        page,
        total,
        data,
        rowsPerPage,
    });
    const onChangePage = handleChangePage({
        onChangePageHandler,
        setPage,
        rowsPerPage,
    });

    const onChangeRowsPerPage = handleChangeRowsPerPage({
        onChangePageHandler,
        setRowsPerPage,
        setPage,
    });

    const tableItemsCount = total || data.length;
    const currPage = tableItemsCount > 0 ? page : 0;
    const selectProps = {
        inputProps: {
            'aria-label': 'rows per page',
        },
    };

    const containerClass = clx('table', variant);

    const mainTableClass = clx('table-main', {
        scrollable: variant === 'scrollable',
    });

    return (
        <div className={mainTableClass}>
            <MUITableContainer className={containerClass} component={Paper}>
                {children}
                <MUITable size="small">
                    {variant !== TableVariants.dark && (
                        <TableHeader columns={columns} setSort={setSort} sort={sort} />
                    )}

                    <TableBodyContainer
                        removedIds={removedIds}
                        data={renderRows}
                        columns={columns}
                    />
                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                rowsPerPageOptions={rowsOptions}
                                colSpan={columns.length}
                                count={tableItemsCount}
                                rowsPerPage={rowsPerPage}
                                page={currPage}
                                SelectProps={selectProps}
                                onChangePage={onChangePage}
                                onChangeRowsPerPage={onChangeRowsPerPage}
                            />
                        </TableRow>
                    </TableFooter>
                </MUITable>
            </MUITableContainer>
        </div>
    );
}
